import * as React from "react";
import {forwardRef, useImperativeHandle, useRef} from "react";

import {Chart, registerables, TimeScale, Tooltip} from "chart.js";
import {Line} from "react-chartjs-2";
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom'
import CursorPlugin from "./cursor";
import {buildTicksForTimeWindow} from "./DsMonitoringUtils";

Chart.register(
    ...registerables,
    zoomPlugin,
    CursorPlugin
);

Tooltip.positioners['topLeft'] = function (elements, eventPosition) {
    // This callback can actually expect elements and eventPosition parameters but we have no use for them
    const chart = this.chart;
    return {
        x: chart.chartArea.left,
        y: 0,
        xAlign: 'left',
        yAlign: 'top',
    };
};

type DsQueueChartProps = {
    datasets: [],
    onZoomStart?: (Chart) => any,
    onZoom?: (Chart) => any,
    onZoomComplete?: (Chart) => any,
};

const DsQueueChart = forwardRef(
    ({
         datasets,
         onZoomStart,
         onZoom,
         onZoomComplete,
     }: DsQueueChartProps,
     ref
    ): JSX.Element => {
        const chartRef = useRef(null);

        useImperativeHandle(ref, () => ({

            resetZoom() {
                chartRef.current.resetZoom();
            },

            getZoom() {
                console.log(`DsQueueChart.getZoom`)
                const xScale = chartRef?.current?.scales?.x;
                return {min: xScale?.min, max: xScale?.max,};
            },

            setZoom(min, max) {
                console.log(`DsQueueChart.setZoom(${min}, ${max})`);
                const scale = chartRef.current?.scales?.x;
                if (!scale) {
                    console.log(`\t return - scale is ${scale}`);
                    return;
                }
                if (scale.min === undefined ||
                    scale.max === undefined) {
                    console.log(`\t return - scale.min:${scale.min} scale.max:${scale.max}`);
                    return;
                }
                if (scale.min === min && scale.max === max) {
                    console.log(`\t return - both min and max didn't change`);
                    return;
                }
                chartRef.current.zoomScale('x', {min: min, max: max});
            },

        }));

        return (
            <div className="chart-area">
                <Line
                    ref={chartRef}
                    data={{datasets: datasets ?? []}}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        animations: false,
                        parsing: false,
                        interaction: {
                            mode: "nearest",
                            axis: 'x',
                            intersect: false,
                            includeInvisible: true
                        },
                        scales: {
                            y: {
                                suggestedMin: 0,
                                suggestedMax: 5,
                                position: 'left',
                                ticks: {
                                    callback(value) {
                                        return `${value.toLocaleString('fra')} ms`; //French locale uses spaces between thousands
                                    }
                                },
                            },
                            y2: {
                                min: 0,
                                max: 1,
                                display: false,
                            },
                            x: {
                                parsing: false,
                                type: 'time',
                                time: {
                                    displayFormats: {
                                        second: "HH:mm:ss",
                                        minute: "HH:mm",
                                        hour: "HH:mm",

                                    },
                                    tooltipFormat: 'yyyy-MM-dd HH:mm:ss'
                                },
                                ticks: {
                                    minRotation: 90,
                                    maxRotation: 90,
                                    includeBounds: true, // Doesn't seem to do anything when using callback
                                    autoSkip: false,
                                    sampleSize: 1, // Only look at the first tick to determine needed space
                                },
                                afterBuildTicks(ts: TimeScale) {
                                    ts.ticks = buildTicksForTimeWindow(ts.min, ts.max);
                                }
                            }
                        },
                        plugins: {
                            cursor: {
                                background: {
                                    enabled: false,
                                    color: 'whiteSmoke',
                                },
                                line: {
                                    width: 1,
                                    dashPattern: [7, 3],
                                    color: '#666',
                                },
                                snap: {
                                    enabled: true,
                                },
                                sync: {
                                    enabled: true,            // enable trace line syncing with other charts
                                    group: 1,                 // chart group
                                    alignVerticalAxes: true,
                                },
                            },
                            decimation: {
                                enabled: true,
                                // algorithm: 'lttb',
                                algorithm: 'min-max',
                                // samples: 512,
                                threshold: 512,
                            },
                            legend: {
                                display: false,
                            },
                            title: {
                                display: false,
                            },
                            tooltip: {
                                enabled: true,
                                position: 'topLeft',
                                caretSize: 0,
                                caretPadding: 0,
                                //animation: false,
                                usePointStyle: true,
                                callbacks: {
                                    labelPointStyle(context) {
                                        return {
                                            pointStyle: 'circle',
                                            rotation: 0
                                        };
                                    },
                                    label(item) {
                                        if (item.dataset.yAxisID === 'y2') {
                                            return `No data at this time`;
                                        }
                                        return `${item.dataset.label} ${item.parsed.y.toLocaleString('fra')} ms`;
                                    },
                                },
                            },
                            zoom: {
                                limits: {
                                    x: {
                                        min: 'original',
                                        max: 'original',
                                        minRange: 5 * 60 * 1000, // 5 minutes
                                    }
                                },
                                zoom: {
                                    wheel: {
                                        enabled: true,
                                    },
                                    drag: {
                                        enabled: true,
                                    },
                                    pinch: {
                                        enabled: true
                                    },
                                    mode: 'x',
                                    onZoomStart,
                                    onZoom,
                                    onZoomComplete
                                },
                            },
                        },
                    }}/>
            </div>
        );

    });

export default DsQueueChart;
